import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import _ from 'lodash';
import {
  TenantTrackingId,
  TrackingDataMap,
} from '../services/analytics/types/trackingDataModel';
import { TrackingUpdateAction } from '../services/analytics/types/actions';
import {
  DEFAULT_TRACKING_DATA,
  getTenantDataRules,
} from '../services/analytics/defaults/defaultTrackingData';
import { createTrackingReducer } from '../services/analytics/trackingReducer';
import { getTenantIdFromEnv } from '../../utils/tenantUtils';
import api from '../../api';
import { TRACKING_ACTION_TYPES } from '../services/analytics/constants/constants';
import {
  loadPersistedTrackingData,
  persistTrackingData,
} from '../services/analytics/trackingSessionStorage';

interface TrackingContextType<T extends TenantTrackingId> {
  tenantTrackingId: T | null;
  latestTrackingData: TrackingDataMap[T] | null;
  dispatchTrackingAction: (action: TrackingUpdateAction) => void;
  sendTrackingData: () => void;
  resetTrackingData: (isSubmitAction?: boolean) => void;
  runOnceTrackingReady: (fn: () => void) => void;
}

const TrackingContext = createContext<
  TrackingContextType<TenantTrackingId> | undefined
>(undefined);

export function TrackingProvider<T extends TenantTrackingId>({
  children,
}: {
  children: React.ReactNode;
}) {
  const tenantId = useMemo(() => {
    const allowedTenantIds: TenantTrackingId[] = Object.keys(
      DEFAULT_TRACKING_DATA
    ) as TenantTrackingId[];
    const tenantIdFromEnv = getTenantIdFromEnv()?.trim();
    return allowedTenantIds.includes(tenantIdFromEnv as TenantTrackingId)
      ? (tenantIdFromEnv as T)
      : null;
  }, []);

  const defaultTrackingData = useMemo(() => {
    if (!tenantId) return null;
    return _.cloneDeep(DEFAULT_TRACKING_DATA[tenantId]);
  }, [tenantId]);

  const [trackingData, dispatch] = useReducer(
    createTrackingReducer<T>(tenantId),
    defaultTrackingData,
    (initialData) =>
      initialData ? loadPersistedTrackingData(initialData) : initialData
  );
  const latestTrackingDataRef = useRef(trackingData);
  const pendingReadyCallbacksRef = useRef<Array<() => void>>([]);

  const isTrackingReady = useCallback(
    () => Boolean(tenantId && latestTrackingDataRef.current?.appId),
    [tenantId]
  );

  const runOnceTrackingReady = useCallback(
    (fn: () => void) => {
      if (isTrackingReady()) {
        fn();
        return;
      }
      pendingReadyCallbacksRef.current.push(fn);
    },
    [isTrackingReady]
  );

  const dispatchTrackingAction = useCallback(
    (action: TrackingUpdateAction) => {
      if (!tenantId) return null;
      dispatch(action);
      latestTrackingDataRef.current = createTrackingReducer<T>(tenantId)(
        latestTrackingDataRef.current,
        action
      );
    },
    [tenantId]
  );

  const sendTrackingData = useCallback(() => {
    if (!tenantId) return;

    api.Public.sendTrackingData(tenantId, latestTrackingDataRef.current).catch(
      (e: unknown) => console.error(e)
    );
  }, [tenantId]);

  const resetTrackingData = useCallback(
    (isSubmitAction = false) => {
      if (!tenantId || !defaultTrackingData) return;

      const tenantDataRules = getTenantDataRules(tenantId);
      const skipResetOnSubmit = tenantDataRules?.skipResetOnSubmit ?? false;
      const shouldReset = isSubmitAction && !skipResetOnSubmit;

      // defer reset to ensure the updated tracking data is sent first
      setTimeout(async () => {
        if (shouldReset) {
          latestTrackingDataRef.current = _.cloneDeep(defaultTrackingData);
          dispatch({
            type: TRACKING_ACTION_TYPES.RESET,
            payload: defaultTrackingData,
          });
        }
      }, 0);
    },
    [tenantId, defaultTrackingData]
  );

  const value = useMemo(
    () => ({
      tenantTrackingId: tenantId,
      latestTrackingData: latestTrackingDataRef.current,
      dispatchTrackingAction,
      sendTrackingData,
      resetTrackingData,
      runOnceTrackingReady,
    }),
    [
      tenantId,
      latestTrackingDataRef.current,
      dispatchTrackingAction,
      sendTrackingData,
      resetTrackingData,
      runOnceTrackingReady,
    ]
  );

  useEffect(() => {
    latestTrackingDataRef.current = trackingData;
  }, [trackingData]);

  useEffect(() => {
    if (!trackingData) return;
    persistTrackingData(trackingData);
  }, [trackingData]);

  useEffect(() => {
    if (pendingReadyCallbacksRef.current.length === 0 || !isTrackingReady()) {
      return;
    }
    const callbacks = pendingReadyCallbacksRef.current;
    pendingReadyCallbacksRef.current = [];
    callbacks.forEach((fn) => fn());
  }, [trackingData, isTrackingReady]);

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
}

export function useTracking() {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTracking must be used within TrackingProvider');
  }
  return context;
}
